"use client";

import { CentPrecisionMoney, Money } from "@commercetools/platform-sdk";
import cn from "clsx";
import { useTranslations } from "next-intl";
import React, { useMemo } from "react";
import ContentLoader from "react-content-loader";

import { CountryCode } from "@/common/constants/contry";
import { LoayltyTier } from "@/features/account/components/loyalty-tier";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { sumPrices, useFormattedPrice } from "@/features/product/hooks/use-price";

type LoaderProps = {
  showDiscount?: boolean;
  className?: string;
  theme?: "light" | "dark";
};

const ProductPriceLoader: React.FC<LoaderProps> = ({
  showDiscount = false,
  className = "",
  theme = "light",
}) => {
  const id = React.useId();
  const width = showDiscount ? 220 : 170;
  const colorTheme = {
    backgroundColor: theme === "light" ? "#EBECEC" : "#3B444A",
    foregroundColor: theme === "light" ? "#F8F8F8" : "#CFC9C9",
  };
  return (
    <ContentLoader
      uniqueKey={id}
      speed={2}
      width={width}
      height={52}
      viewBox={`0 0 ${width} 52`}
      className={cn("block h-full", className)}
      {...colorTheme}
    >
      <rect x="0" y="0" rx="0" ry="0" width="95" height="35" />
      <rect x="0" y="42" rx="0" ry="0" width="170" height="10" />
      {showDiscount && <rect x="110" y="15" rx="0" ry="0" width="80" height="20" />}
    </ContentLoader>
  );
};

export const PriceInfo: React.FC<
  {
    showError?: boolean;
    className?: string;
    testId?: string;
    showLoyaltyTier?: boolean;
  } & Pick<LoaderProps, "theme">
> = ({ showError = true, className = "", theme = "light", testId, showLoyaltyTier }) => {
  const t = useTranslations("common");
  const {
    pricingResult,
    isLoading,
    error,
    isError,
    deliveryOption,
    taxCategory,
    artworkCheckPrice,
  } = useStoreProduct();

  const taxRateAmount = useMemo(() => {
    if (!taxCategory) return 0;
    // Right now we will use default GB country code on product page
    const taxRate = taxCategory.rates.find((r) => r.country === (CountryCode.GB as string));
    return taxRate?.amount || 0;
  }, [taxCategory]);

  const artworkCheckPricing = {
    ...pricingResult?.listPrice.total,
    centAmount: artworkCheckPrice ?? 0,
  } as CentPrecisionMoney;

  const totalPrice = sumPrices(
    pricingResult?.listPrice.total,
    deliveryOption?.price,
    artworkCheckPricing,
  );
  const salePrice = sumPrices(
    pricingResult?.salePrice.total,
    deliveryOption?.price,
    artworkCheckPricing,
  );

  const taxedPrice = {
    ...salePrice,
    centAmount: (salePrice?.centAmount ?? 0) * (1 + taxRateAmount),
  } as Money;

  const price = useFormattedPrice(totalPrice);
  const grossPrice = useFormattedPrice(taxedPrice);

  const unitPrice = useFormattedPrice(pricingResult?.salePrice.unit, "en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

  const discountTotal =
    salePrice && totalPrice && salePrice?.centAmount < totalPrice?.centAmount
      ? salePrice
      : undefined;
  const discount = useFormattedPrice(discountTotal);

  const isEmptyState = (!showError && isError) || (!pricingResult && !isError);

  if (isLoading || isEmptyState) {
    return (
      <div className={cn("lg:h-[60px]", className)}>
        <ProductPriceLoader showDiscount={!!discount} theme={theme} />
      </div>
    );
  }

  if (isError && showError && error instanceof Error) {
    return <div className="bg-danger rounded p-2 text-white">{error?.message}</div>;
  }

  return (
    <div className="flex items-baseline">
      {showLoyaltyTier && <LoayltyTier iconOnly={true} />}
      <div
        data-testid={testId}
        className={cn("text-grey-90 justify-left flex flex-col items-start", className)}
      >
        <p className="mb-0 flex items-baseline">
          <span id="configurator-discount-price" className="text-3xl font-semibold lg:text-4xl">
            {discount}
          </span>
          <span
            className={`${discount ? "ml-3 text-xl font-normal line-through  lg:mr-4 lg:text-2xl" : "text-3xl font-semibold lg:text-4xl"}`}
            id="configurator-total-price"
          >
            {price}
          </span>
        </p>
        <p className="mb-3 flex text-xs lg:text-sm">
          <span className="whitespace-nowrap">
            {taxRateAmount ? `${grossPrice} ${t("text-inc-vat")}` : t("text-vat-exempt")}
          </span>
          <span className="mx-2">|</span>
          <span className="whitespace-nowrap">
            {t("text-unit-price")}: {unitPrice}
          </span>
        </p>
      </div>
    </div>
  );
};
